// ── Reusable stateful hooks ──
import { useState, useEffect } from "react";
import { useApp } from "./appContext";

export const usePagination = (rows, pageSize=50) => {
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const paginated = rows.slice((page-1)*pageSize, page*pageSize);
  const reset = () => setPage(1);
  return { paginated, page, setPage, totalPages, total:rows.length, reset };
};

export const useSortable = (rows, defaultKey='', defaultDir='asc') => {
  const [sortKey, setSortKey] = useState(defaultKey);
  const [sortDir, setSortDir] = useState(defaultDir);
  const toggle = key => {
    if (sortKey === key) setSortDir(d => d==='asc'?'desc':'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };
  // number only if the ENTIRE string is numeric — "2026-07-24" must NOT parse to 2026
  const asNumber = v => {
    const s = String(v).replace(/,/g,'').trim();
    return /^-?\d+(\.\d+)?$/.test(s) ? Number(s) : NaN;
  };
  const sorted = [...rows].sort((a,b) => {
    const av = a[sortKey], bv = b[sortKey];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;      // nulls/blanks always sort last
    if (bv == null) return -1;
    const an = asNumber(av), bn = asNumber(bv);
    let cmp;
    if (!isNaN(an) && !isNaN(bn)) {
      cmp = an - bn;                                   // true numbers
    } else {
      const ad = Date.parse(av), bd = Date.parse(bv);
      cmp = (!isNaN(ad) && !isNaN(bd))
        ? ad - bd                                      // dates → compare by actual time
        : String(av).localeCompare(String(bv));        // fall back to text
    }
    return sortDir==='asc' ? cmp : -cmp;
  });
  return { sorted, sortKey, sortDir, toggle };
};

export const useForm = (initial) => {
  const [f, setF] = useState(initial);
  const set = k => v => setF(p=>({...p,[k]:v}));
  // setF is exposed as a 3rd element for callers that need to merge/replace the whole form
  // (e.g. re-seeding from a fresh server fetch). Existing `const [f,set]=useForm()` is unaffected.
  return [f, set, setF];
};

// Like useForm, but for EDIT forms: on open it re-reads the record fresh from the
// DB (getRecord?table&id) and merges it over the initial (cached) values, so a form
// never renders a stale list row after a prior save. `table` is the record's table
// (the same one the form passes to saveRec). Adds nothing for new records (no id).
// Drop-in: `const [f, set] = useFreshForm(initial || blank, "Terms");`
export const useFreshForm = (initial, table) => {
  const [f, set, setF] = useForm(initial);
  const ctx = useApp();
  const api = ctx && ctx.api;
  const rid = initial && initial.id;
  useEffect(() => {
    if (!rid || !api || !table) return;
    let alive = true;
    api.get("getRecord?table=" + encodeURIComponent(table) + "&id=" + encodeURIComponent(rid))
      .then(fresh => { if (alive && fresh && typeof fresh === "object") setF(prev => ({ ...prev, ...fresh })); })
      .catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rid]);
  return [f, set, setF];
};
